import { useForm } from 'react-hook-form'
import { useCallback } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useEffect } from 'react'
import { useNotification } from 'components/Notification'
import { updateUserUploads } from 'services/userUploadRequests'
import useRequestFormFileUpload from './useRequestFormFileUpload'
import {
  transformFormDataToRequestPayload,
  transformRequestToFormValues,
  invalidateRequestQueries,
  DEFAULT_CREATE_AND_EDIT_FORM_VALUES,
} from '../utils'

/**
 * Hook for managing the edit request form
 * @param {function} onClose - callback function to close the modal
 * @param {object} request - the request data to edit
 * @returns {object} form controls and handlers
 */
function useEditRequestForm(onClose, request) {
  const { createNotification } = useNotification()
  const queryClient = useQueryClient()

  // Form setup with request data
  const formMethods = useForm({
    defaultValues: DEFAULT_CREATE_AND_EDIT_FORM_VALUES,
    mode: 'onChange',
  })

  const { control, handleSubmit, formState, setValue, reset } = formMethods

  // Use shared file upload functionality with initial files and form integration
  // Memoize the getter and setter to ensure stable references for the upload hook
  const getUrlFieldValues = useCallback(() => formMethods.getValues('urls'), [formMethods])
  const setUrlFieldValue = useCallback(value => formMethods.setValue('urls', value), [formMethods])

  const {
    pendingFiles,
    uploadedFiles,
    handleFileSelect,
    handleFileRemove,
    resetFileState,
    updateFileState,
    secureFileUpload,
  } = useRequestFormFileUpload({
    initialFiles: request?.filePaths,
    getUrlFieldValues,
    setUrlFieldValue,
  })

  // Initialize form with request data
  useEffect(() => {
    if (request) {
      const formValues = transformRequestToFormValues(request)
      reset(formValues)

      // Update file state when request changes
      updateFileState(request.filePaths || [])
    }
  }, [request, reset, updateFileState])

  const handleClose = () => {
    reset()
    resetFileState()
    onClose()
  }

  const updateRequestMutation = useMutation(
    async formData => {
      const requestData = transformFormDataToRequestPayload(formData)
      return await updateUserUploads(request.id, requestData)
    },
    {
      onSuccess: data => {
        createNotification({
          message: 'Request updated successfully',
          variant: 'success',
        })
        // Invalidate queries to refresh the requests list
        invalidateRequestQueries(queryClient)
        handleClose()
      },
      onError: error => {
        createNotification({
          message: error?.message || 'Failed to update request',
          variant: 'error',
        })
      },
    },
  )

  const handleFormSubmit = handleSubmit(formData => {
    updateRequestMutation.mutate(formData)
  })

  const isSubmitting = updateRequestMutation.isLoading

  return {
    control,
    formState,
    setValue,
    handleSubmit: handleFormSubmit,
    handleClose,
    isSubmitting,
    reset,
    // File upload functionality
    handleFileSelect,
    handleFileRemove,
    uploadedFiles,
    pendingFiles,
    secureFileUpload,
  }
}

export default useEditRequestForm
